import { useEffect, useMemo, useState } from 'react';
import { Plus, Receipt as ReceiptIcon, Search, RefreshCw, Coins, FileWarning } from 'lucide-react';
import { Input, Select } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { ExpensesTable } from '../../components/expenses/ExpensesTable';
import { ExpenseFormModal } from '../../components/expenses/ExpenseFormModal';
import { ExpenseDetailModal } from '../../components/expenses/ExpenseDetailModal';
import { useExpenses } from '../../hooks/useExpenses';
import { useExpenseCategories } from '../../hooks/useExpenseCategories';
import { useVehicles } from '../../hooks/useVehicles';
import { useDrivers } from '../../hooks/useDrivers';
import { useAuth } from '../../context/AuthContext';
import type { Expense, ExpenseScope } from '../../types/expense';
import type { ExpenseCreateDto } from '../../lib/api/endpoints';
import { uploadExpenseReceipt, getExpense } from '../../lib/api/endpoints';
import { expenseFromDto } from '../../lib/api/mappers';
import { comprimirRecibo } from '../../utils/receiptImage';
import { aISODate, rangoDeMes, rangoDeDias } from '../../utils/finance';
import {
  EXPENSE_DATE_FILTERS,
  rangoDeFiltro,
  puedeRegistrar,
  puedeCorregir,
  dentroDeVentanaEdicion,
  puedeSubirRecibo,
  sumarMontos,
  formatearCOP,
  sinRecibo,
  type ExpenseDateFilter,
} from '../../utils/expenses';

/**
 * FE-12 — la pantalla de gastos.
 *
 * El backend ya acota la visibilidad por rol en `GET /expenses` —el Operador ve
 * solo los suyos, el Supervisor su sub-unidad—, así que esta página no vuelve a
 * filtrar: hacerlo sería fingir una autorización de cliente. Lo que sí decide
 * el rol es qué botones existen, y aun así el servidor puede responder 403.
 *
 * **El registro son DOS llamadas**, y no se pueden hacer una: el endpoint del
 * recibo cuelga del id del gasto, que no existe hasta que el `POST /expenses`
 * responde. Por eso el recibo es opcional y por eso, si la segunda falla, el
 * gasto **queda registrado** y se avisa de que hay que completarlo — no hay
 * `DELETE /expenses` con el que deshacerlo, y tampoco debería haberlo: un
 * registro contable no se borra.
 */
export default function ExpensesPage() {
  const { user } = useAuth();
  const [dateFilter, setDateFilter] = useState<ExpenseDateFilter | 'mes' | 'rango'>('month');
  const [mes, setMes] = useState(() => aISODate(new Date()).slice(0, 7));
  const [rangoDesde, setRangoDesde] = useState(() => aISODate(new Date(new Date().getFullYear(), new Date().getMonth(), 1)));
  const [rangoHasta, setRangoHasta] = useState(() => aISODate(new Date()));
  const [scopeFilter, setScopeFilter] = useState<'' | ExpenseScope>('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [vehicleFilter, setVehicleFilter] = useState('');
  const [search, setSearch] = useState('');

  // Un mes concreto o un rango de días: los límites son días LOCALES enteros
  // (00:00 del primero a 23:59:59 del último), igual que `rangoDeFiltro`.
  const filtro = useMemo(() => ({
    ...(dateFilter === 'mes' ? rangoDeMes(mes) : dateFilter === 'rango' ? rangoDeDias(rangoDesde, rangoHasta) : rangoDeFiltro(dateFilter)),
    categoryId: categoryFilter || undefined,
    vehicleId: vehicleFilter || undefined,
  }), [dateFilter, mes, rangoDesde, rangoHasta, categoryFilter, vehicleFilter]);

  const { expenses, isLoading, error, reload, registrar, corregir, reemplazar } = useExpenses(filtro);
  const { categories } = useExpenseCategories();
  const { vehicles } = useVehicles();
  const { drivers } = useDrivers();

  const [formAbierto, setFormAbierto] = useState(false);
  const [detalleId, setDetalleId] = useState<string | null>(null);
  const [corrigiendoId, setCorrigiendoId] = useState<string | null>(null);
  const [avisoRecibo, setAvisoRecibo] = useState<string | null>(null);

  useEffect(() => { document.title = 'Gastos | Paitron Radar'; }, []);

  // Los modales trabajan sobre el gasto VIVO de la lista, no sobre una copia
  // congelada al abrirlos: tras corregir, el objeto cambia y el detalle tiene
  // que reflejarlo sin cerrarse.
  const detalle = expenses.find(e => e.id === detalleId) ?? null;
  const corrigiendo = expenses.find(e => e.id === corrigiendoId) ?? null;

  // Clase (del vehículo / de operación) → categoría: cada categoría pertenece a
  // UNA clase (`expense_category.scope`), así que no se solapan. La clase se
  // filtra en cliente mirando la categoría del gasto; la categoría va al API.
  const scopeDe = useMemo(() => new Map(categories.map(c => [c.id, c.scope])), [categories]);
  const categoriasOfrecidas = scopeFilter ? categories.filter(c => c.scope === scopeFilter) : categories;

  const visibles = expenses.filter(e => {
    if (scopeFilter && scopeDe.get(e.categoryId) !== scopeFilter) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      e.categoryLabel.toLowerCase().includes(q) ||
      (e.notes ?? '').toLowerCase().includes(q) ||
      (e.vehiclePlate ?? '').toLowerCase().includes(q) ||
      (e.driverName ?? '').toLowerCase().includes(q)
    );
  });

  // La suma es de lo que se está MIRANDO, incluida la búsqueda en cliente: un
  // total que no cuadre con las filas de debajo es peor que no tener total.
  const suma = sumarMontos(visibles.map(e => e.amount));
  const faltanRecibos = visibles.filter(sinRecibo).length;

  const abrirCorreccion = (expense: Expense) => {
    setDetalleId(null);
    setCorrigiendoId(expense.id);
  };

  /**
   * Registra el gasto y, si hay foto, la sube después.
   *
   * Si la subida falla, **no se deshace el registro**: se avisa nombrando el
   * gasto para que alguien lo complete desde su detalle. Tragarse el error
   * dejaría un gasto sin soporte contable que nadie sabría que está incompleto.
   */
  const registrarConRecibo = async (data: ExpenseCreateDto, recibo: File | null) => {
    setAvisoRecibo(null);
    const gasto = await registrar(data);
    if (!recibo) return gasto;
    try {
      const listo = await comprimirRecibo(recibo);
      await uploadExpenseReceipt(gasto.id, listo);
      reemplazar(expenseFromDto(await getExpense(gasto.id)));
    } catch {
      setAvisoRecibo(
        `El gasto de ${formatearCOP(gasto.amount)} quedó registrado, pero la foto del recibo no se pudo subir. Ábrelo y adjúntala desde su detalle.`,
      );
    }
    return gasto;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
        {[
          { icon: <ReceiptIcon size={22} />, color: 'var(--accent)', valor: String(visibles.length), label: 'Gastos en pantalla' },
          { icon: <Coins size={22} />, color: '#10B981', valor: formatearCOP(suma), label: 'Suma de lo filtrado' },
          { icon: <FileWarning size={22} />, color: '#F59E0B', valor: String(faltanRecibos), label: 'Sin recibo' },
        ].map(s => (
          <div key={s.label} style={{ background: 'var(--bg-surface)', padding: '1.25rem', borderRadius: 'var(--radius-xl)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div style={{ width: '44px', height: '44px', borderRadius: 'var(--radius-md)', background: `${s.color}1F`, color: s.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              {s.icon}
            </div>
            <div style={{ minWidth: 0 }}>
              <span style={{ fontSize: '20px', fontWeight: 800, color: 'var(--text-primary)', display: 'block' }}>{s.valor}</span>
              <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{s.label}</span>
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ flex: '1 1 220px', minWidth: '180px' }}>
          <Input
            label="Buscar"
            placeholder="Categoría, nota, placa o conductor…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            leftIcon={<Search size={15} />}
          />
        </div>

        <div style={{ flex: '0 1 170px', minWidth: '150px' }}>
          <Select
            label="Periodo"
            value={dateFilter}
            onChange={e => setDateFilter(e.target.value as ExpenseDateFilter | 'mes' | 'rango')}
            options={[
              ...EXPENSE_DATE_FILTERS.map(f => ({ value: f.id, label: f.label })),
              { value: 'mes', label: 'Un mes concreto…' },
              { value: 'rango', label: 'Rango de fechas…' },
            ]}
          />
        </div>
        {dateFilter === 'mes' && (
          <div style={{ flex: '0 1 170px', minWidth: '150px' }}>
            <Input label="Mes" type="month" value={mes} onChange={e => setMes(e.target.value)} max={aISODate(new Date()).slice(0, 7)} />
          </div>
        )}
        {dateFilter === 'rango' && (
          <>
            <div style={{ flex: '0 1 160px', minWidth: '140px' }}>
              <Input label="Desde" type="date" value={rangoDesde} max={rangoHasta || undefined} onChange={e => setRangoDesde(e.target.value)} />
            </div>
            <div style={{ flex: '0 1 160px', minWidth: '140px' }}>
              <Input label="Hasta" type="date" value={rangoHasta} min={rangoDesde || undefined} onChange={e => setRangoHasta(e.target.value)} />
            </div>
          </>
        )}

        <div style={{ flex: '0 1 170px', minWidth: '150px' }}>
          <Select
            label="Clase"
            value={scopeFilter}
            onChange={e => {
              const scope = e.target.value as '' | ExpenseScope;
              setScopeFilter(scope);
              // Una categoría de la otra clase no puede seguir elegida.
              if (scope && categoryFilter && scopeDe.get(categoryFilter) !== scope) setCategoryFilter('');
            }}
            options={[
              { value: '', label: 'Todas las clases' },
              { value: 'VEHICLE', label: 'Del vehículo' },
              { value: 'OPERATION', label: 'De operación' },
            ]}
          />
        </div>

        <div style={{ flex: '0 1 190px', minWidth: '150px' }}>
          <Select
            label="Categoría"
            value={categoryFilter}
            onChange={e => setCategoryFilter(e.target.value)}
            options={[
              { value: '', label: scopeFilter ? 'Todas las de la clase' : 'Todas las categorías' },
              // El filtro ofrece TODAS, incluidas las desactivadas: un gasto
              // viejo conserva su categoría aunque hoy esté retirada, y
              // esconderla lo haría imposible de encontrar.
              ...categoriasOfrecidas.map(c => ({ value: c.id, label: c.active ? c.label : `${c.label} (inactiva)` })),
            ]}
          />
        </div>

        <div style={{ flex: '0 1 190px', minWidth: '150px' }}>
          <Select
            label="Vehículo"
            value={vehicleFilter}
            onChange={e => setVehicleFilter(e.target.value)}
            options={[
              { value: '', label: 'Todos los vehículos' },
              ...vehicles.map(v => ({ value: v.id, label: v.plate })),
            ]}
          />
        </div>

        <Button variant="ghost" leftIcon={<RefreshCw size={15} />} onClick={reload} disabled={isLoading}>
          Actualizar
        </Button>

        {puedeRegistrar(user?.role) && (
          <Button leftIcon={<Plus size={16} />} onClick={() => setFormAbierto(true)}>
            Registrar gasto
          </Button>
        )}
      </div>

      {error && (
        <div style={{ padding: '0.875rem 1rem', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 'var(--radius-lg)', color: '#EF4444', fontSize: '13px' }}>
          No se pudieron cargar los gastos: {error.message}
        </div>
      )}

      {avisoRecibo && (
        <div style={{ display: 'flex', gap: '0.625rem', alignItems: 'flex-start', padding: '0.875rem 1rem', background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 'var(--radius-lg)', color: '#F59E0B', fontSize: '13px' }}>
          <FileWarning size={16} style={{ flexShrink: 0, marginTop: '1px' }} />
          <span style={{ flex: 1 }}>{avisoRecibo}</span>
          <button type="button" onClick={() => setAvisoRecibo(null)} style={{ border: 'none', background: 'transparent', color: 'inherit', cursor: 'pointer', fontWeight: 700 }}>
            Entendido
          </button>
        </div>
      )}

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}><Spinner /></div>
      ) : visibles.length === 0 ? (
        <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', padding: '2rem', textAlign: 'center', fontSize: '13px', color: 'var(--text-muted)' }}>
          {search.trim() ? 'Ningún gasto coincide con la búsqueda.' : scopeFilter ? 'Sin gastos de esa clase en este periodo.' : 'Sin gastos registrados en este periodo.'}
        </div>
      ) : (
        <ExpensesTable
          expenses={visibles}
          role={user?.role}
          onVerDetalle={e => setDetalleId(e.id)}
          onCorregir={abrirCorreccion}
        />
      )}

      <ExpenseDetailModal
        expense={detalle}
        onClose={() => setDetalleId(null)}
        onCorregir={abrirCorreccion}
        puedeCorregir={puedeCorregir(user?.role)}
        ventanaVencida={detalle ? !dentroDeVentanaEdicion(detalle, user?.role) : false}
        puedeSubirRecibo={puedeSubirRecibo(user?.role)}
        onReciboSubido={reemplazar}
      />

      <ExpenseFormModal
        isOpen={formAbierto || !!corrigiendo}
        onClose={() => { setFormAbierto(false); setCorrigiendoId(null); }}
        expense={corrigiendo}
        // El catálogo COMPLETO: el formulario ya se queda con las activas más
        // la del propio gasto, que al corregir puede estar desactivada.
        categorias={categories}
        vehicles={vehicles}
        drivers={drivers}
        driverAutomatico={user?.role === 'operator'}
        onRegistrar={registrarConRecibo}
        onCorregir={corregir}
      />
    </div>
  );
}
